import api from './api'

// Chosen in the switcher by a super admin to drop the account filter entirely.
// Not a real account, so it is never in the accounts list and has to survive
// SET_ACCOUNTS re-resolving the selection by id.
export const ALL_ACCOUNTS = { id: 'all', name: 'All accounts', type: 'All' }

// Permission-aware auth module.
// - `role` / `isOwner` / `isAdmin` etc. are GONE. Replaced by:
//     canRoute(name)   — true if the active account grants that route
//     canAction(name)  — true if the active account grants that action
//     isSuperAdmin     — kept (reads the user's global flag)
// - Permissions come from login response: accounts[].pivot.route_access + action_access

export default {
  state: {
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    token: localStorage.getItem('token'),
    accounts: JSON.parse(localStorage.getItem('accounts') || 'null'),
    activeAccount: JSON.parse(localStorage.getItem('activeAccount') || 'null'),
    expires_at: JSON.parse(localStorage.getItem('expires_at') || 'null'),
    cartCount: (() => {
      try {
        const cart = JSON.parse(localStorage.getItem('shop_cart') || '{"items":[]}')
        return (cart.items || []).reduce((s, i) => s + (Number(i.qty) || 0), 0)
      } catch { return 0 }
    })()
  },

  mutations: {
    SET_AUTH(state, payload) {
      state.user = payload.user
      state.token = payload.token
      state.accounts = payload.accounts
      state.activeAccount = payload.accounts?.[0] || null
      state.expires_at = payload.expires_at

      localStorage.setItem('user', JSON.stringify(payload.user))
      localStorage.setItem('token', payload.token)
      localStorage.setItem('accounts', JSON.stringify(payload.accounts))
      localStorage.setItem('activeAccount', JSON.stringify(state.activeAccount))
      localStorage.setItem('expires_at', JSON.stringify(payload.expires_at))
    },

    SET_ACTIVE_ACCOUNT(state, account) {
      state.activeAccount = account
      localStorage.setItem('activeAccount', JSON.stringify(account))
    },

    /**
     * Replace the account list without disturbing the session.
     *
     * `accounts` used to be written once, at login, and never again. So an
     * admin who granted someone a second account saw nothing change until
     * that user logged out and back in — and since the account switcher only
     * renders when accounts.length > 1, the switcher itself stayed invisible.
     * That read as "multi-account doesn't work".
     *
     * The active account is re-resolved against the new list by id so a
     * refresh never silently moves the user to a different account, and never
     * leaves them pointed at one they have just lost access to.
     */
    SET_ACCOUNTS(state, accounts) {
      state.accounts = accounts || []
      localStorage.setItem('accounts', JSON.stringify(state.accounts))

      const currentId = state.activeAccount?.id
      const stillThere = currentId === ALL_ACCOUNTS.id
        ? ALL_ACCOUNTS
        : state.accounts.find(a => a.id === currentId)
      const next = stillThere || state.accounts[0] || null

      state.activeAccount = next
      localStorage.setItem('activeAccount', JSON.stringify(next))
    },

    SET_CART_COUNT(state, count) {
      state.cartCount = count
    },

    LOGOUT(state) {
      state.user = null
      state.token = null
      state.accounts = null
      state.activeAccount = null
      state.expires_at = null

      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('accounts')
      localStorage.removeItem('activeAccount')
      localStorage.removeItem('expires_at')
    }
  },

  actions: {
    login({ commit }, payload) {
      commit('SET_AUTH', payload)
    },

    logout({ commit }) {
      commit('LOGOUT')
    },

    switchAccount({ commit, state }, account) {
      // Accept an id or a whole account object — call sites pass both.
      const resolved = typeof account === 'object' && account !== null
        ? account
        : (account === ALL_ACCOUNTS.id
            ? ALL_ACCOUNTS
            : (state.accounts || []).find(a => a.id === Number(account)))

      if (!resolved) return
      commit('SET_ACTIVE_ACCOUNT', resolved)
    },

    /**
     * Re-read the caller's accounts from the API. Safe to call on every
     * authenticated app load: it cannot log anyone out and it cannot change
     * which account is active unless that account is gone.
     */
    async refreshAccounts({ commit, state }) {
      if (!state.token) return
      try {
        const res = await api.get('/accounts/available')
        const accounts = res.data?.accounts
        if (Array.isArray(accounts)) commit('SET_ACCOUNTS', accounts)
      } catch {
        // Offline or a transient 5xx — keep whatever we already had. The
        // cached list is stale at worst, and this runs on every load.
      }
    },

    updateCartCount({ commit }) {
      try {
        const cart = JSON.parse(localStorage.getItem('shop_cart') || '{"items":[]}')
        const count = (cart.items || []).reduce((s, i) => s + (Number(i.qty) || 0), 0)
        commit('SET_CART_COUNT', count)
      } catch {
        commit('SET_CART_COUNT', 0)
      }
    }
  },

  getters: {
    isAuthenticated: state => !!state.token,
    isSuperAdmin: state => !!state.user?.is_super_admin,
    cartCount: state => state.cartCount,

    /** Permission: can the user navigate to a given named route in the active account? */
    canRoute: (state, getters) => (routeName) => {
      if (!state.token) return false
      if (getters.isSuperAdmin) return true
      const list = state.activeAccount?.pivot?.route_access ?? state.activeAccount?.route_access ?? []
      return Array.isArray(list) && list.includes(routeName)
    },

    /** Permission: can the user perform a given action in the active account? */
    canAction: (state, getters) => (actionName) => {
      if (!state.token) return false
      if (getters.isSuperAdmin) return true
      const list = state.activeAccount?.pivot?.action_access ?? state.activeAccount?.action_access ?? []
      return Array.isArray(list) && list.includes(actionName)
    },

    /**
     * Legacy compatibility: "is the user privileged to do admin-type things?"
     * True if they have any write-class action. Used by shop components that
     * still have an isPrivileged computed; keeps the old UX working.
     */
    isPrivileged: (state, getters) => {
      if (!state.token) return false
      if (getters.isSuperAdmin) return true
      const list = state.activeAccount?.pivot?.action_access ?? state.activeAccount?.action_access ?? []
      return Array.isArray(list) && ['add','edit','delete','approve','complete'].some(a => list.includes(a))
    }
  }
}
